import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Bell, BellOff, Shield, CheckCircle, XCircle } from 'lucide-react';
import { safeLog } from '@/utils/safeLog';

interface NotificationPermissionPromptProps {
  onComplete: (granted: boolean) => void;
  userRole: 'student' | 'teacher' | 'admin';
}

export function NotificationPermissionPrompt({ onComplete, userRole }: NotificationPermissionPromptProps) {
  const [isRequesting, setIsRequesting] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'pending' | 'granted' | 'denied'>('pending');

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    
    try {
      // Check if the browser supports notifications
      if (!('Notification' in window)) {
        alert('This browser does not support notifications');
        setPermissionStatus('denied');
        onComplete(false);
        return;
      }

      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setPermissionStatus('granted');
        // Show a test notification
        new Notification('ShieldED Haven', {
          body: 'Notifications are now enabled! You\'ll receive important safety alerts.',
          icon: '/logo.png',
          badge: '/logo.png'
        });
        setTimeout(() => onComplete(true), 1500);
      } else {
        setPermissionStatus('denied');
        setTimeout(() => onComplete(false), 1500);
      }
    } catch (error) {
      safeLog.error('Error requesting notification permission:', error);
      setPermissionStatus('denied');
      onComplete(false);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleSkip = () => {
    setPermissionStatus('denied');
    onComplete(false);
  };

  const getNotificationBenefits = () => {
    switch (userRole) {
      case 'student':
        return [
          'Instant safety alerts and emergency notifications',
          'Reminders for digital wellness breaks',
          'Updates on classroom activities and assignments',
          'Cyberbullying prevention tips and resources'
        ];
      case 'teacher':
        return [
          'Immediate alerts about student safety concerns',
          'Cyberbullying incident notifications',
          'Student wellness status updates',
          'System security and policy updates'
        ];
      case 'admin':
        return [
          'Critical system alerts and security notifications',
          'School-wide safety incident reports',
          'Administrative updates and compliance alerts',
          'User activity and system health notifications'
        ];
      default:
        return [];
    }
  };

  return (
    <div className="min-h-screen min-w-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full">
              {permissionStatus === 'granted' ? (
                <CheckCircle className="w-8 h-8 text-white" />
              ) : permissionStatus === 'denied' ? (
                <XCircle className="w-8 h-8 text-white" />
              ) : (
                <Bell className="w-8 h-8 text-white" />
              )}
            </div>
          </div>
          <CardTitle className="text-2xl">
            {permissionStatus === 'granted' 
              ? 'Notifications Enabled!' 
              : permissionStatus === 'denied'
              ? 'Notifications Disabled'
              : 'Enable Notifications'}
          </CardTitle>
          <CardDescription>
            {permissionStatus === 'granted'
              ? 'You\'ll now receive important safety alerts and updates.'
              : permissionStatus === 'denied'
              ? 'You can enable notifications later in your browser settings.'
              : 'Stay informed with important safety alerts and updates'}
          </CardDescription>
        </CardHeader>
        
        {permissionStatus === 'pending' && (
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">You'll receive notifications for:</h3>
              <ul className="space-y-2">
                {getNotificationBenefits().map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Bell className="w-4 h-4 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Privacy Notice</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Notifications are sent securely and only contain essential safety information. 
                    You can disable them at any time in your browser settings.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button 
                onClick={handleRequestPermission} 
                disabled={isRequesting}
                className="flex-1"
              >
                {isRequesting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Requesting...
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4 mr-2" />
                    Enable Notifications
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={handleSkip} disabled={isRequesting} style={{ backgroundColor: '#000', color: '#fff' }}>
                <BellOff className="w-4 h-4 mr-2" />
                Skip
              </Button>
            </div>
          </CardContent>
        )}

        {(permissionStatus === 'granted' || permissionStatus === 'denied') && (
          <CardContent>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                {permissionStatus === 'granted' 
                  ? 'Setting up your personalized experience...'
                  : 'You can change this setting anytime in your browser preferences.'}
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}